import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Search, Loader2, Sparkles } from 'lucide-react'

interface BrowseProductsModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (message: string) => void
  isSubmitting?: boolean
}

const CHANNEL_OPTIONS = ['Display', 'DOOH', 'Video', 'High Impact', 'Native', 'Rich Media', 'Social']
const DELIVERY_OPTIONS = ['Any', 'guaranteed', 'non-guaranteed']
const PRICING_OPTIONS = ['Any', 'CPM', 'CPC', 'Flat']

export function BrowseProductsModal({ isOpen, onClose, onSubmit, isSubmitting = false }: BrowseProductsModalProps) {
  const [brief, setBrief] = useState('')
  const [market, setMarket] = useState('')
  const [channels, setChannels] = useState<Set<string>>(new Set())
  const [deliveryType, setDeliveryType] = useState('Any')
  const [pricingModel, setPricingModel] = useState('Any')
  const [budget, setBudget] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [objectives, setObjectives] = useState('')

  useEffect(() => {
    if (isOpen) {
      setBrief('')
      setMarket('')
      setChannels(new Set())
      setDeliveryType('Any')
      setPricingModel('Any')
      setBudget('')
      setStartDate('')
      setEndDate('')
      setObjectives('')
    }
  }, [isOpen])

  const toggleChannel = (ch: string) => {
    setChannels(prev => {
      const next = new Set(prev)
      if (next.has(ch)) next.delete(ch)
      else next.add(ch)
      return next
    })
  }

  const handleSubmit = () => {
    const parts: string[] = ['Find advertising products for a media buy with the following requirements:']

    if (brief.trim()) parts.push(`Brief: ${brief.trim()}`)
    if (market.trim()) parts.push(`Market: ${market.trim()}`)
    if (channels.size > 0) parts.push(`Channels: ${Array.from(channels).join(', ')}`)
    if (deliveryType !== 'Any') parts.push(`Delivery Type: ${deliveryType}`)
    if (pricingModel !== 'Any') parts.push(`Pricing Model: ${pricingModel}`)
    if (budget.trim()) parts.push(`Budget: ${budget.trim()}`)
    if (startDate || endDate) {
      const timing = [startDate && `from ${startDate}`, endDate && `to ${endDate}`].filter(Boolean).join(' ')
      parts.push(`Timing: ${timing}`)
    }
    if (objectives.trim()) parts.push(`Objectives: ${objectives.trim()}`)

    if (parts.length === 1) {
      parts.push('Please recommend suitable products based on the campaign context.')
    }

    onSubmit(parts.join('\n'))
  }

  const hasAnyInput = brief || market || channels.size > 0 || deliveryType !== 'Any' || pricingModel !== 'Any' || budget || startDate || endDate || objectives

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
          >
            {/* Header */}
            <div className="relative flex-shrink-0 px-6 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" />
                    Discover
                  </div>
                  <h2 className="text-2xl font-bold mt-1">Browse products</h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Describe your requirements to find matching products.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shadow-sm"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 scrollbar-thin">
              {/* Brief */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Campaign Brief</label>
                <textarea
                  value={brief}
                  onChange={e => setBrief(e.target.value)}
                  placeholder="Describe the campaign goals and context..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Market + Budget row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Market</label>
                  <input
                    type="text"
                    value={market}
                    onChange={e => setMarket(e.target.value)}
                    placeholder="e.g. Singapore, US"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Budget</label>
                  <input
                    type="text"
                    value={budget}
                    onChange={e => setBudget(e.target.value)}
                    placeholder="e.g. $30,000"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Channels */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Channels</label>
                <div className="flex flex-wrap gap-1.5">
                  {CHANNEL_OPTIONS.map(ch => (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => toggleChannel(ch)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        channels.has(ch)
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {ch}
                    </button>
                  ))}
                </div>
              </div>

              {/* Delivery Type + Pricing Model row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Delivery Type</label>
                  <select
                    value={deliveryType}
                    onChange={e => setDeliveryType(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {DELIVERY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt === 'Any' ? 'Any' : opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Pricing Model</label>
                  <select
                    value={pricingModel}
                    onChange={e => setPricingModel(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {PRICING_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Timing row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Objectives */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Specific Objectives</label>
                <textarea
                  value={objectives}
                  onChange={e => setObjectives(e.target.value)}
                  placeholder="e.g. Maximize reach among young adults, drive CTR above 0.5%..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex-shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-3.5 w-3.5" />
                    {hasAnyInput ? 'Find Products' : 'Browse All'}
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
